/** Validacion del RUC peruano (11 digitos con digito de control, segun SUNAT).
 *
 * Replica la regla de la web (`frontend/src/utils/peruRuc.ts`): valida la
 * forma del numero, no su existencia en el padron. Los pesos fijos son
 * `5,4,3,2,7,6,5,4,3,2`; el digito verificador es `11 - (suma % 11)` con las
 * equivalencias `10 -> 0` y `11 -> 1`.
 */

/** Pesos que SUNAT aplica a los 10 primeros digitos, en orden. */
const RUC_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2] as const;

/** Longitud exacta del RUC. */
const RUC_LENGTH = 11;

/** Calcula el digito verificador de los 10 primeros digitos. */
export function rucCheckDigit(firstTenDigits: string): number {
  const sum = firstTenDigits
    .split('')
    .reduce((total, digit, index) => total + Number(digit) * RUC_WEIGHTS[index], 0);
  const remainder = 11 - (sum % 11);
  if (remainder === 10) return 0;
  if (remainder === 11) return 1;
  return remainder;
}

/** Motivo por el que un RUC no pasa la validacion. */
export type PeruRucProblem = 'empty' | 'length' | 'alphanumeric' | 'checksum';

/** Clasifica el problema de un RUC para indicar que corregir. */
export function peruRucProblem(value: string | null | undefined): PeruRucProblem | null {
  const digits = (value ?? '').trim();
  if (digits === '') return 'empty';
  if (!/^\d+$/.test(digits)) return 'alphanumeric';
  if (digits.length !== RUC_LENGTH) return 'length';
  if (rucCheckDigit(digits.slice(0, 10)) !== Number(digits[10])) return 'checksum';
  return null;
}

/** Indica si `value` tiene la forma de un RUC peruano valido. */
export function isValidPeruRuc(value: string | null | undefined): boolean {
  return peruRucProblem(value) === null;
}

const RUC_MESSAGES: Record<PeruRucProblem, string> = {
  empty: 'El RUC es obligatorio',
  length: 'Ingresa 11 digitos, solo numeros',
  alphanumeric: 'Ingresa 11 digitos, solo numeros',
  checksum: 'El ultimo digito no coincide. Verifica el RUC en tu comprobante',
};

/** Mensaje de error para Yup, o `undefined` cuando el RUC es valido. */
export function peruRucError(value: string | null | undefined): string | undefined {
  const problem = peruRucProblem(value);
  return problem === null ? undefined : RUC_MESSAGES[problem];
}

/** Alcance declarado en la interfaz: valida el digito, no la existencia. */
export const RUC_SCOPE_HINT =
  '11 digitos; se valida el digito de control, no su existencia en SUNAT';
